import { useState } from 'react';
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { MapPin, MapPinOff, Home, DollarSign } from "lucide-react";
import { PlacesAutocomplete } from "@/components/service-form/PlacesAutocomplete";
import { AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";

interface ServiceModeSettingProps {
  serviceMode: string;
  setServiceMode: (mode: string) => void;
  travelFee: string;
  setTravelFee: (fee: string) => void;
  serviceArea: string;
  onLocationChange: (location: string) => void;
  requiresScheduling: boolean;
}

export const ServiceModeSetting = ({ 
  serviceMode, 
  setServiceMode, 
  travelFee, 
  setTravelFee, 
  serviceArea, 
  onLocationChange,
  requiresScheduling = true // Default to true for backward compatibility
}: ServiceModeSettingProps) => {
  const { t } = useTranslation();
  const [showFeeInput, setShowFeeInput] = useState(
    serviceMode === "client-location" && !!travelFee
  );

  const handleModeChange = (value: string) => {
    setServiceMode(value);
    if (value !== "client-location") {
      setTravelFee("");
      setShowFeeInput(false);
    }
  };

  // Only show location settings if scheduling is required
  if (!requiresScheduling) {
    return (
      <div className="p-4 bg-gray-50 rounded-lg">
        <div className="text-sm text-gray-600 flex items-start gap-2">
          <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-gray-900">{t('services.locationSettingsDisabled')}</p>
            <p>{t('services.locationSettingsDisabledDescription')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <RadioGroup 
          value={serviceMode} 
          onValueChange={handleModeChange}
          className="flex flex-col space-y-2"
        >
          <div className="flex items-start space-x-2 bg-white p-3 rounded-lg border border-gray-100 hover:border-gray-200 transition-colors">
            <RadioGroupItem value="my-location" id="my-location" className="mt-1" />
            <div className="flex-1">
              <Label 
                htmlFor="my-location" 
                className="flex items-center text-base font-medium cursor-pointer"
              >
                <Home className="mr-2 h-5 w-5 text-gray-600" />
                {t('services.atMyLocation')}
              </Label>
              <p className="text-sm text-gray-600 mt-1">
                {t('services.atMyLocationDescription')}
              </p>
              
              {serviceMode === "my-location" && (
                <div className="mt-3">
                  <Label htmlFor="my-location-input" className="text-sm mb-1 block">
                    {t('services.yourServiceLocation')}
                  </Label>
                  <PlacesAutocomplete
                    value={serviceArea}
                    onChange={onLocationChange}
                    placeholder={t('services.enterBusinessAddress')}
                    icon={<MapPin className="h-5 w-5 text-gray-400" />}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {t('services.addressShownToClients')}
                  </p>
                </div>
              )}
            </div>
          </div>
          
          <div className="flex items-start space-x-2 bg-white p-3 rounded-lg border border-gray-100 hover:border-gray-200 transition-colors">
            <RadioGroupItem value="client-location" id="client-location" className="mt-1" />
            <div className="flex-1">
              <Label 
                htmlFor="client-location" 
                className="flex items-center text-base font-medium cursor-pointer"
              >
                <MapPin className="mr-2 h-5 w-5 text-gray-600" />
                {t('services.atClientLocation')}
              </Label>
              <p className="text-sm text-gray-600 mt-1">
                {t('services.atClientLocationDescription')}
              </p>
              
              {serviceMode === "client-location" && (
                <div className="mt-3 space-y-3">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="travel-fee-checkbox"
                      checked={showFeeInput}
                      onChange={(e) => {
                        setShowFeeInput(e.target.checked);
                        if (!e.target.checked) {
                          setTravelFee("");
                        }
                      }}
                      className="h-4 w-4 text-gebeya-pink border-gray-300 rounded focus:ring-gebeya-pink"
                    />
                    <label htmlFor="travel-fee-checkbox" className="ml-2 text-sm text-gray-700">
                      {t('services.chargeTravelFee')}
                    </label>
                  </div>
                  
                  {showFeeInput && (
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        type="number"
                        placeholder={t('services.travelFeeAmount')}
                        value={travelFee}
                        onChange={(e) => setTravelFee(e.target.value)}
                        className="pl-9"
                        min="0"
                        step="any"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {t('services.travelFeeDescription')}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          
          <div className="flex items-start space-x-2 bg-white p-3 rounded-lg border border-gray-100 hover:border-gray-200 transition-colors">
            <RadioGroupItem value="no-location" id="no-location" className="mt-1" />
            <div>
              <Label 
                htmlFor="no-location" 
                className="flex items-center text-base font-medium cursor-pointer"
              >
                <MapPinOff className="mr-2 h-5 w-5 text-gray-600" />
                {t('services.noSpecificLocation')}
              </Label>
              <p className="text-sm text-gray-600 mt-1">
                {t('services.noSpecificLocationDescription')}
              </p>
            </div>
          </div>
        </RadioGroup>
      </div>
    </div>
  );
};
